import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "Content", "dropdownContentRef", "menuWidth", "minMenuWidth", "dropdownStyles"],
  _excluded2 = ["animateOnMount", "animateOnToggle", "aria-expanded", "aria-labelledby", "aria-invalid", "arrowColor", "arrowSize", "autoPlacement", "Button", "buttonRef", "buttonSize", "buttonText", "buttonUse", "caretRenderer", "children", "closeOnOutsideClick", "closeOnTargetLeave", "Content", "dataDropdown", "hasSearchbox", "dropdownContentRef", "icon", "id", "isDisplayOptimized", "locked", "menuWidth", "minMenuWidth", "onChangePlacement", "onClick", "onFocusLeave", "onOpenChange", "onOpenComplete", "open", "placement", "popoverRef", "popoverProps", "readOnly", "shouldCloseOnClick", "_useNativeButton", "responsive", "theme", "buttonStyles", "cssStyles"];
import { Component } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import { Button as ButtonComponent } from '../Button';
import { Clickable } from '../Clickable';
import { FloatingButtonGroupContext } from '../FloatingButtonGroup/FloatingButtonGroupContext';
import { callIfPossible } from '../../utils/functions';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import ShareButton from '../ShareButton/ShareButton';
import { Popover } from '../Popover';
import { cancelSchedulerCallback, requestSchedulerCallback } from '../../utils/Timers';
import { render as customRenderer } from '../../utils/customRenderer';
import { uniqueId } from '../../utils/uniqueId';
import { DropdownCaret, StyledCaret } from './DropdownCaret';
import styled from 'foundations-theming/utils';
import { withTheme } from 'styled-components';
import { AbstractDropdownProvider } from './AbstractDropdownProvider';
import { StyledLink } from '../Link/Link';
import { StyledContentWrapper } from '../Typeahead/TypeaheadResultsItem';
import { DropdownContext } from '../Dropdown/DropdownContext';
import { deepMerge } from '../../utils/deepMerge';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { StyledImage } from '../Image/Image';
import { StyledStatusDot } from '../StatusDot/StatusDot';
import { StyledView } from '../../../components/View/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MIN_MENU_WIDTH = 144;
const getArrowSize = buttonUse => {
  if (buttonUse.match(/link|transparent/)) {
    return 'medium';
  }
  return 'none';
};
const getPlacement = (placement, computedArrowSize) => {
  if (placement) {
    return placement;
  }
  // Center if we have an arrow, otherwise flush left against the target
  if (computedArrowSize !== 'none') {
    return 'bottom';
  }
  return 'bottom right';
};
const StyledDropdownButtonLabel = styled.span.withConfig({
  displayName: "AbstractDropdown__StyledDropdownButtonLabel",
  componentId: "sc-3z1946-0"
})(({
  $noIcon,
  theme
}) => {
  return {
    display: $noIcon ? 'block' : 'flex',
    alignItems: 'center',
    '& svg:not([data-icon-name])': {
      marginInlineEnd: theme.space['200']
    },
    '& svg([data-icon-name])': {
      marginBlockStart: theme.space['200']
    },
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto',
    marginInlineEnd: !$noIcon ? '0.25rem' : theme.space['0'],
    minWidth: 0,
    textAlign: 'left',
    paddingBlock: theme.space['50'],
    marginBlock: `-${theme.space['50']}`,
    hyphens: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    maxWidth: '100%',
    [`& ${StyledContentWrapper}`]: {
      '& > *:first-child:not(:has(svg[data-icon-name]))': {
        marginBlockStart: theme.space['0'],
        alignItems: 'center'
      },
      [`&:has(${StyledImage}):not(:has(svg[data-icon-name]))`]: {
        marginBlock: `-${theme.space['25']}`
      },
      '& > *:first-child:has(svg[data-icon-name])': {
        marginBlockEnd: `-${theme.space['100']}`,
        marginBlockStart: theme.space['0'],
        marginInlineEnd: theme.space['200']
      }
    },
    [`& ${StyledImage}`]: {
      marginInlineStart: theme.space['100'],
      marginInlineEnd: '6px' // no token for 6px
    }
  };
});
export const DropdownContent = styled.div.withConfig({
  displayName: "AbstractDropdown__DropdownContent",
  componentId: "sc-3z1946-1"
})(({
  $styleOverrides,
  $cssStyles,
  $dropdownCssStyles,
  theme
}) => {
  return deepMerge({
    borderRadius: 'inherit',
    [`& ${StyledStatusDot}`]: {
      marginInlineStart: theme.space['100'],
      marginInlineEnd: '6px' // no token for 6px
    },
    [`& ${StyledView}:has(${StyledStatusDot})`]: {
      marginInlineEnd: '6px' // no token for 6px
    },
    '&&': Object.assign({}, $dropdownCssStyles, $cssStyles)
  }, $styleOverrides !== null && $styleOverrides !== void 0 ? $styleOverrides : {});
});
export const StyledButtonContents = styled.span.withConfig({
  displayName: "AbstractDropdown__StyledButtonContents",
  componentId: "sc-3z1946-2"
})(({
  buttonSize,
  buttonUse,
  isDisplayOptimized,
  theme
}) => Object.assign({
  alignItems: 'center',
  display: 'flex',
  width: isDisplayOptimized ? 'auto' : '100%',
  justifyContent: 'space-between'
}, buttonSize !== 'xs' && buttonSize !== 'extra-small' && buttonSize !== 'small' && buttonSize !== 'sm' && buttonUse !== 'link' && {
  lineHeight: '16px'
}, {
  [`& [${StyledLink}]`]: {
    display: 'inline-flex'
  },
  [`& ${StyledCaret}`]: {
    marginInlineStart: theme.space['0']
  }
}));
const getStyle = (menuWidth, controlWidth) => ({
  width: menuWidth || controlWidth,
  minWidth: menuWidth === 'auto' ? controlWidth : undefined
});
const renderButtonLabel = (buttonText, Icon, readOnly) => {
  return /*#__PURE__*/_jsxs(StyledDropdownButtonLabel, {
    $noIcon: Icon === null || readOnly,
    children: [Icon && /*#__PURE__*/_jsx(Icon, {}), buttonText == null || buttonText === '' ? '\u200B' : buttonText]
  });
};
const getButtonStyleOverrides = ({
  buttonUse,
  isDisplayOptimized,
  hasSearchbox,
  dataDropdown,
  ariaInvalid,
  theme,
  buttonStyles,
  cssStyles
}) => {
  // We want to preserve the override styles from display optimized FormControl variant
  if (isDisplayOptimized) {
    return {
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: theme.typography['body-100'].fontSize,
      minHeight: '30px',
      lineHeight: theme.typography['body-100'].lineHeight,
      paddingBlock: theme.space['200'],
      paddingInlineEnd: theme.space['0'],
      paddingInlineStart: theme.space['0'],
      textDecoration: 'none',
      '&:hover:not([aria-disabled="true"]), &:focus:not([aria-disabled="true"]), &:focus-visible:not([aria-disabled="true"]), &:active:not([aria-disabled="true"]), &[aria-disabled="true"]': {
        backgroundColor: 'transparent',
        border: 'none',
        outline: 'none',
        textDecoration: 'none'
      },
      // This is a workaround to ensure the text is vertically centered
      '& > span': {
        display: 'inline-flex'
      }
    };
  }
  const baseStyles = {
    textAlign: 'left',
    '&[data-action="close"]': {
      padding: theme.space['0']
    }
  };
  const focusStyles = {
    outline: 'none !important',
    borderColor: 'rgba(0,208,228,0.5) !important',
    // no token
    boxShadow: '0 0 4px 1px rgba(0,208,228,0.3), 0 0 0 1px #00d0e4 !important' // no token
  };
  return Object.assign({}, baseStyles, buttonUse !== 'link' && ariaInvalid && {
    '&[aria-invalid="true"]': {
      borderColor: theme.color['border-alert-default'],
      boxShadow: `0 0 0 1px ${theme.color['border-alert-default']}`
    }
  }, !hasSearchbox && dataDropdown && {
    display: 'inline-block',
    alignItems: 'center'
  }, {
    // '&:not(:first-child)': {
    //   marginInlineStart: `-${theme.space['25']}`,
    // },
    [`&&:focus`]: buttonUse === 'form' || buttonUse === 'form-on-dark' ? focusStyles : {}
  }, buttonUse === 'link' ? {
    padding: theme.space['0'],
    '&:hover': {
      textDecoration: 'none !important'
    }
  } : {}, buttonUse === 'transparent' ? {
    paddingBlock: '11px' // no token
  } : {}, {
    // Only apply buttonStyles and cssStyles to buttons that have data-dropdown-open attribute
    '&&[data-dropdown-open]': Object.assign({}, buttonStyles, cssStyles)
  });
};
class AbstractDropdownCore extends Component {
  constructor(props) {
    super(props);
    this.state = {
      controlWidth: 0
    };
    this.measureControlWidth = () => {
      // Make the menu width match up with the anchor width
      const {
        buttonRef,
        open
      } = this.props;
      const controlEl = buttonRef.current;
      if (controlEl && open) {
        const controlWidth = this.computeControlWidth(controlEl);
        if (controlWidth !== this.state.controlWidth) {
          this.setState({
            controlWidth
          });
        }
      }
    };
    this.isFocusWithinDropdown = () => {
      const {
        buttonRef
      } = this.props;
      const activeElement = document.activeElement;
      const dropdownNode = document.getElementById(this._contentId);
      if (!activeElement) {
        return false;
      }
      if (activeElement === buttonRef.current) {
        return true;
      } else if (dropdownNode && dropdownNode.contains(activeElement)) {
        return true;
      }
      return false;
    };
    this.handleButtonClick = evt => {
      const {
        locked,
        open,
        onClick
      } = this.props;
      evt.preventDefault();
      evt.stopPropagation();
      const scrollPos = window.scrollY;
      callIfPossible(onClick, evt);
      if (!locked) {
        const nextOpen = !open;
        this.setIsOpen(nextOpen);
      }
      requestAnimationFrame(() => {
        if (window.scrollY !== scrollPos) {
          window.scrollTo(0, scrollPos);
        }
      });
    };
    this.handleWindowResize = () => {
      cancelSchedulerCallback(this._resizeFrame);
      this._resizeFrame = requestSchedulerCallback(this.measureControlWidth);
    };
    this.handleKeyDown = evt => {
      // Handle keydown events from both the button and the dropdown
      const {
        onKeyDown,
        open
      } = this.props;
      const {
        key
      } = evt;

      // Check if the event is from the button
      const isFromButton = evt.currentTarget.tagName === 'BUTTON';
      if (!open && isFromButton && (key === 'Enter' || key === ' ')) {
        this.setIsOpen(true);
        evt.preventDefault();
        evt.stopPropagation();
      } else if (open && key === 'Escape') {
        this.setIsOpen(false);
        evt.stopPropagation();
      }
      callIfPossible(onKeyDown, evt);
    };
    this.setIsOpen = nextOpen => {
      const {
        onOpenChange,
        readOnly
      } = this.props;
      if (!onOpenChange || readOnly) {
        return;
      }
      onOpenChange(syntheticEvent(nextOpen));
    };
    this.focusOnControl = () => {
      const {
        buttonRef
      } = this.props;
      const controlNode = buttonRef.current;
      if (!controlNode) return;
      const scrollPos = window.scrollY;
      controlNode.focus({
        preventScroll: true
      });
      if (window.scrollY !== scrollPos) {
        window.scrollTo(0, scrollPos);
      }
    };
    this.computeControlWidth = controlEl => {
      const {
        open
      } = this.props;
      if (!controlEl || !open) {
        return 0;
      }
      const exactWidth = controlEl.getBoundingClientRect().width;
      const roundedWidth = window.chrome ? Math.floor(exactWidth) : exactWidth;
      return roundedWidth - 2; // account for 1px border added by UIPopover
    };
    this.renderContent = () => {
      const _this$props = this.props,
        {
          children,
          Content,
          dropdownContentRef,
          menuWidth,
          minMenuWidth = MIN_MENU_WIDTH,
          dropdownStyles
        } = _this$props,
        rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
      const controlWidth = Math.max(this.state.controlWidth, minMenuWidth);
      const {
        tokenProps
      } = filterProps(rest);
      const cssStyles = useMapTokenPropsToCSS(tokenProps);
      const dropdownCssStyles = useMapTokenPropsToCSS(dropdownStyles || {});
      return /*#__PURE__*/_jsx(DropdownContext.Consumer, {
        children: ({
          dropdownStyleOverrides
        }) => /*#__PURE__*/_jsx(DropdownContent, {
          id: this._contentId,
          onKeyDown: this.handleKeyDown,
          ref: dropdownContentRef,
          role: "presentation",
          style: this._getStyle(menuWidth, controlWidth),
          $styleOverrides: dropdownStyleOverrides,
          $cssStyles: cssStyles,
          $dropdownCssStyles: dropdownCssStyles,
          children: Content ? /*#__PURE__*/_jsx(Content, {}) : children
        })
      });
    };
    this._buttonContentId = uniqueId('abstractdropdown-button-content-');
    this._contentId = uniqueId('abstractdropdown-content-');
    this._getStyle = memoizeOne(getStyle);
    this.shouldCloseOnClick = this.shouldCloseOnClick.bind(this);
  }
  componentDidMount() {
    const {
      open
    } = this.props;
    if (open) {
      this.measureControlWidth();
      window.addEventListener('resize', this.handleWindowResize);
    }
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      open
    } = this.props;

    // ⚠️ This condition prevents infinite loops! ⚠️
    if (prevState.controlWidth === this.state.controlWidth) {
      this.measureControlWidth();
    }
    if (prevProps.open && !open && this.isFocusWithinDropdown()) {
      this.focusOnControl();
    }
    if (!prevProps.open && open) {
      window.addEventListener('resize', this.handleWindowResize);
    } else if (prevProps.open && !open) {
      window.removeEventListener('resize', this.handleWindowResize);
      cancelSchedulerCallback(this._resizeFrame);
    }
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
    cancelSchedulerCallback(this._resizeFrame);
  }
  shouldCloseOnClick(evt) {
    const {
      buttonRef,
      shouldCloseOnClick
    } = this.props;

    // If `shouldCloseOnClick` is provided and returns a non-null value, defer to it
    if (shouldCloseOnClick) {
      const shouldClose = shouldCloseOnClick(evt);
      if (shouldClose != null) return shouldClose;
    }
    const target = evt.target;
    const controlNode = buttonRef.current;
    if (!controlNode) return null;

    // Ignore clicks on any <label> that contain the button
    let parentEl = controlNode;
    while (parentEl.parentElement) {
      parentEl = parentEl.parentElement;
      if (parentEl.tagName === 'LABEL' && parentEl.contains(target)) {
        return false;
      }
    }

    // Ignore clicks on any <label> whose "for" attr points to the button
    const id = controlNode.id;
    if (id) {
      const labelsForControl = document.querySelectorAll(`label[for="${id}"]`);
      for (let i = 0; i < labelsForControl.length; i++) {
        if (labelsForControl[i].contains(target)) {
          return false;
        }
      }
    }
    return null;
  }
  render() {
    const _this$props2 = this.props,
      {
        animateOnMount,
        animateOnToggle,
        'aria-expanded': ariaExpanded,
        'aria-labelledby': ariaLabelledby,
        'aria-invalid': ariaInvalid = false,
        arrowColor,
        arrowSize,
        autoPlacement = 'dropdown',
        Button,
        buttonRef,
        buttonSize = 'medium',
        buttonText,
        buttonUse = 'secondary',
        caretRenderer = /*#__PURE__*/_jsx(DropdownCaret, {
          buttonUse: buttonUse,
          buttonSize: buttonSize
        }),
        closeOnOutsideClick = true,
        closeOnTargetLeave,
        dataDropdown,
        hasSearchbox,
        icon: Icon,
        id,
        isDisplayOptimized,
        locked: __locked = false,
        minMenuWidth: __minMenuWidth = MIN_MENU_WIDTH,
        onFocusLeave,
        onOpenChange,
        onOpenComplete,
        open = false,
        placement,
        popoverRef,
        popoverProps,
        readOnly = false,
        _useNativeButton,
        responsive,
        theme,
        buttonStyles,
        cssStyles
      } = _this$props2,
      rest = _objectWithoutPropertiesLoose(_this$props2, _excluded2);
    const computedArrowSize = arrowSize || getArrowSize(buttonUse);
    const computedPlacement = getPlacement(placement, computedArrowSize);
    const renderedButtonLabel = renderButtonLabel(buttonText, Icon);
    const renderedCaret = customRenderer(caretRenderer, {
      use: buttonUse,
      buttonSize
    });
    const ButtonType = Button || (buttonUse === 'unstyled' ? Clickable : ButtonComponent);
    const buttonProps = buttonUse === 'unstyled' ? {} : {
      _useNativeButton,
      size: buttonSize,
      use: buttonUse,
      responsive: isDisplayOptimized ? false : responsive
    };
    const computedAriaExpanded = !!(ariaExpanded != null ? ariaExpanded : open) || undefined;
    const buttonStyleOverrides = getButtonStyleOverrides({
      buttonUse,
      isDisplayOptimized,
      hasSearchbox: hasSearchbox !== null && hasSearchbox !== void 0 ? hasSearchbox : false,
      dataDropdown: dataDropdown !== null && dataDropdown !== void 0 ? dataDropdown : false,
      ariaInvalid,
      theme: theme,
      buttonStyles: buttonStyles !== null && buttonStyles !== void 0 ? buttonStyles : {},
      cssStyles: cssStyles !== null && cssStyles !== void 0 ? cssStyles : {}
    });
    return /*#__PURE__*/_jsx(AbstractDropdownProvider, {
      buttonStyleOverrides: buttonStyleOverrides,
      children: /*#__PURE__*/_jsx(Popover, Object.assign({
        animateOnMount: animateOnMount,
        animateOnToggle: animateOnToggle,
        arrowColor: arrowColor,
        arrowSize: computedArrowSize,
        autoPlacement: typeof autoPlacement === 'string' ? true : autoPlacement,
        closeOnOutsideClick: closeOnOutsideClick,
        closeOnTargetLeave: closeOnTargetLeave,
        Content: this.renderContent,
        onFocusLeave: onFocusLeave,
        onOpenChange: onOpenChange,
        onOpenComplete: onOpenComplete,
        open: open,
        placement: computedPlacement,
        preserveDisplayOptimized: isDisplayOptimized,
        popoverRef: popoverRef
      }, popoverProps, {
        children: /*#__PURE__*/_jsx(ButtonType, Object.assign({}, rest, buttonProps, {
          "aria-expanded": computedAriaExpanded,
          "aria-haspopup": true,
          "aria-invalid": ariaInvalid,
          "aria-labelledby": ariaLabelledby ? `${ariaLabelledby} ${this._buttonContentId}` : this._buttonContentId,
          "aria-readonly": readOnly,
          "aria-owns": this._contentId,
          "data-dropdown": dataDropdown,
          "data-dropdown-open": open,
          id: id,
          isDisplayOptimized: isDisplayOptimized,
          onClick: this.handleButtonClick,
          onKeyDown: this.handleKeyDown,
          buttonRef: buttonRef,
          children: /*#__PURE__*/_jsxs(StyledButtonContents, {
            buttonSize: buttonSize,
            buttonUse: buttonUse,
            id: this._buttonContentId,
            isDisplayOptimized: isDisplayOptimized,
            children: [renderedButtonLabel, !readOnly && (typeof caretRenderer === 'function' ? renderedCaret({
              use: buttonUse
            }) : caretRenderer)]
          })
        }))
      }))
    });
  }
}
AbstractDropdownCore.contextType = FloatingButtonGroupContext;
export const AbstractDropdown = withTheme(ShareButton(AbstractDropdownCore));